// frontend/src/pages/WorkshopCatalog.js
// Workshop "Catalogue pièces": add spare parts directly to the suggestion
// engine (no quotation, no client, no notification), and review/edit/delete them.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import API_URL from '../api/baseURL';
import {
  Page, Panel, Button, Badge, Table, Tr, Td, Field,
  EmptyState, ErrorNote, Note, inputClass, selectClass,
} from '../components/ui/Iparc';
import { fmt } from '../theme/iparc';

const BLANK = { vehicleId: '', designation: '', brand: '', reference: '', price: '', type: 'OEM' };

const WorkshopCatalog = () => {
  const { token } = useAuth();

  const [vehicles, setVehicles] = useState([]);
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [editingId, setEditingId] = useState(null);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const fetchVehicles = async () => {
    try {
      const res = await axios.get(`${API_URL}/vehicles/assigned`, authHeader);
      setVehicles(Array.isArray(res.data) ? res.data : []);
    } catch (_) { /* non-fatal */ }
  };

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API_URL}/catalog/entries`, authHeader);
      setEntries(Array.isArray(res.data) ? res.data : []);
    } catch (_) { /* non-fatal */ }
  };

  useEffect(() => {
    if (!token) return;
    fetchVehicles();
    fetchEntries();
    // eslint-disable-next-line
  }, [token]);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleAdd = async () => {
    setError(''); setOkMsg('');
    if (!form.vehicleId || !form.designation.trim()) {
      return setError('Véhicule et désignation sont requis.');
    }
    try {
      setLoading(true);
      await axios.post(`${API_URL}/catalog/entry`, {
        vehicleId: form.vehicleId,
        designation: form.designation.trim(),
        brand: form.brand.trim(),
        reference: form.reference.trim(),
        price: parseFloat(form.price) || 0,
        type: form.type,
      }, authHeader);
      // keep the vehicle selected to make adding several parts fast
      setForm((f) => ({ ...f, designation: '', brand: '', reference: '', price: '' }));
      setOkMsg('Pièce ajoutée au catalogue.');
      fetchEntries();
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors de l'ajout.");
    } finally {
      setLoading(false);
    }
  };

  // Load an existing entry into the form for editing.
  const startEdit = (e) => {
    setError(''); setOkMsg('');
    setEditingId(e._id);
    setForm({
      vehicleId: e.vehicle?._id || e.vehicle || '',
      designation: e.rawName || e.canonicalName || '',
      brand: e.brand || '',
      reference: e.partNumber || '',
      price: e.price != null ? String(e.price) : '',
      type: e.type || 'OEM',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm((f) => ({ ...f, designation: '', brand: '', reference: '', price: '' }));
    setError(''); setOkMsg('');
  };

  const handleUpdate = async () => {
    setError(''); setOkMsg('');
    if (!form.designation.trim()) return setError('La désignation est requise.');
    try {
      setLoading(true);
      await axios.put(`${API_URL}/catalog/entry/${editingId}`, {
        designation: form.designation.trim(),
        brand: form.brand.trim(),
        reference: form.reference.trim(),
        price: parseFloat(form.price) || 0,
        type: form.type,
      }, authHeader);
      setEditingId(null);
      setForm((f) => ({ ...f, designation: '', brand: '', reference: '', price: '' }));
      setOkMsg('Pièce mise à jour.');
      fetchEntries();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la mise à jour.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette pièce du catalogue ?')) return;
    try {
      await axios.delete(`${API_URL}/catalog/entry/${id}`, authHeader);
      setEntries((list) => list.filter((e) => e._id !== id));
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression.');
    }
  };

  const vehLabel = (v) => `${v.plateNumber || ''} — ${v.brand || ''} ${v.model || ''}`.trim();
  const canSubmit = !loading && !!form.vehicleId && !!form.designation.trim();

  return (
    <Page title="Catalogue pièces" subtitle="Atelier">
      <p className="text-[12.5px] text-[#8F877B] -mt-2 max-w-[640px]">
        Ajoutez des pièces (marque, référence, prix) pour alimenter les suggestions de devis.
        Aucune notification n'est envoyée — pas de devis, pas de client.
      </p>

      {error && <ErrorNote>{error}</ErrorNote>}
      {okMsg && <Note tone="ok">{okMsg}</Note>}

      <Panel title={editingId ? 'Modifier la pièce' : 'Ajouter une pièce'}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3.5">
          <Field label="Véhicule" required className="lg:col-span-2">
            <select name="vehicleId" value={form.vehicleId} onChange={handleChange}
                    disabled={!!editingId} className={selectClass}>
              <option value="">Sélectionner…</option>
              {vehicles.map((v) => (
                <option key={v._id} value={v._id}>{vehLabel(v)}</option>
              ))}
            </select>
          </Field>
          <Field label="Désignation" required className="lg:col-span-2">
            <input name="designation" value={form.designation} onChange={handleChange}
                   className={inputClass} placeholder="Ex : plaquettes de frein avant" />
          </Field>
          <Field label="Type">
            <select name="type" value={form.type} onChange={handleChange} className={selectClass}>
              <option value="OEM">OEM (origine)</option>
              <option value="AFTERMARKET">Adaptable</option>
            </select>
          </Field>
          <Field label="Marque pièce">
            <input name="brand" value={form.brand} onChange={handleChange}
                   className={inputClass} placeholder="Ex : Bosch" />
          </Field>
          <Field label="Référence" className="lg:col-span-2">
            <input name="reference" value={form.reference} onChange={handleChange}
                   className={inputClass} placeholder="Ex : OF-940" />
          </Field>
          <Field label="Prix atelier (FCFA)" className="lg:col-span-2">
            <input name="price" type="number" value={form.price} onChange={handleChange}
                   className={inputClass} placeholder="0" />
          </Field>
          <div className="lg:col-span-2 flex items-end gap-2.5">
            <Button
              variant="primary"
              onClick={editingId ? handleUpdate : handleAdd}
              disabled={editingId ? loading || !form.designation.trim() : !canSubmit}
            >
              {loading
                ? (editingId ? 'Mise à jour…' : 'Ajout…')
                : (editingId ? 'Mettre à jour' : 'Ajouter')}
            </Button>
            {editingId && <Button onClick={cancelEdit} disabled={loading}>Annuler</Button>}
          </div>
        </div>
      </Panel>

      <Panel title={`Pièces enregistrées${entries.length ? ` · ${entries.length}` : ''}`} bodyClass="">
        {entries.length === 0 ? (
          <EmptyState
            title="Aucune pièce au catalogue"
            hint="Les pièces ajoutées ici sont proposées automatiquement lors de la création d'un devis."
          />
        ) : (
          <Table head={['Véhicule', 'Désignation', 'Type', 'Marque', 'Référence', 'Prix', '']} minWidth={900}>
            {entries.map((e) => (
              <Tr key={e._id}>
                <Td>
                  <span className="font-['Space_Grotesk',sans-serif] text-[12.5px] tracking-[.02em] text-[#F4F1EA]">
                    {e.vehicle?.plateNumber || '—'}
                  </span>
                  <span className="block text-[11px] text-[#6E665C] mt-0.5">
                    {e.vehicle?.brand} {e.vehicle?.model}
                  </span>
                </Td>
                <Td className="text-[#F4F1EA]">{e.rawName || e.canonicalName}</Td>
                <Td>
                  {e.type === 'OEM'
                    ? <Badge tone="ok">OEM</Badge>
                    : e.type === 'AFTERMARKET'
                      ? <Badge tone="warn">Adaptable</Badge>
                      : <span className="text-[#5C554C]">—</span>}
                </Td>
                <Td>{e.brand || '—'}</Td>
                <Td>{e.partNumber || '—'}</Td>
                <Td mono>
                  {fmt(e.price)} <span className="text-[10.5px] text-[#6E665C]">F</span>
                </Td>
                <Td className="text-right whitespace-nowrap">
                  <Button variant="quiet" className="h-[26px] px-2" onClick={() => startEdit(e)}>
                    Éditer
                  </Button>
                  <Button
                    variant="quiet"
                    className="h-[26px] px-2 text-[#E2574B] hover:text-[#EC7267]"
                    onClick={() => handleDelete(e._id)}
                  >
                    Supprimer
                  </Button>
                </Td>
              </Tr>
            ))}
          </Table>
        )}
      </Panel>
    </Page>
  );
};

export default WorkshopCatalog;
